import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

/**
 * The chat header's reply-language control — the way OUT for an operator
 * who picked the wrong language, or who ignored the one-time question.
 *
 * The agent asks each operator once, in code, which of English / Russian
 * / Hungarian it should reply in, and the answer is captured as a button
 * click rather than parsed out of free text (see
 * `chat-agent/language_preference.py`). This menu reuses that exact
 * mechanism: clicking an item calls `onChoose(code)`, which the chat
 * hook sends as `{answer: {questionId: 'chat-language', value: code}}` —
 * the same wire shape the one-time question's buttons produce, handled
 * by the same server branch, validated against the same three-value
 * enum. There is deliberately no second write path and no free-text
 * field anywhere: a mis-stored language code persists and silently
 * steers every future session, so the only values that can ever reach
 * the store are the three this list and the server agree on.
 *
 * SCOPE: this changes the language the ASSISTANT replies in. It is not
 * dashboard localisation — no other part of the UI is translated, and
 * the labels here are intentionally in their own languages rather than
 * translated into the current one.
 *
 * Labels in the language itself, not in English: an operator who reads
 * only Hungarian has to be able to find Hungarian.
 */

/** Shared contract with `language_preference.LANGUAGE_QUESTION_ID`. */
export const LANGUAGE_QUESTION_ID = 'chat-language';

/** Shared contract with `language_preference.SUPPORTED_LANGUAGES`. */
export const LANGUAGE_CHOICES = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'hu', label: 'Magyar' },
];

function ChatLanguageMenu({ onChoose, disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on an outside click or Escape. Without this the panel stays
  // open behind the thread and swallows the next click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = useCallback((value) => {
    setOpen(false);
    onChoose(value);
  }, [onChoose]);

  return (
    <span className="chat-widget-language" ref={rootRef}>
      <button
        type="button"
        className="chat-widget-header-new"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Language the assistant replies in"
      >
        <i className="fa fa-language" aria-hidden="true" />
        <span>Reply language</span>
      </button>
      {open && (
        <span className="chat-widget-language-menu" role="menu">
          {LANGUAGE_CHOICES.map((choice) => (
            <button
              key={choice.value}
              type="button"
              role="menuitem"
              className="chat-widget-language-item"
              onClick={() => choose(choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export default ChatLanguageMenu;
